import { useEffect, useState } from "react";
import {
  Box, Paper, Button, Alert, TextField, InputAdornment,
  Dialog, DialogContent, DialogActions, CircularProgress,
  FormControlLabel, Switch, Grid2 as Grid,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { CustomerPicker } from "@/components/Pickers";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { insuranceSchemesApi, customersApi, type InsuranceSchemeDto, type CustomerDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

/**
 * Insurance scheme master — a payer's plan (TPA product, government scheme, corporate policy).
 * The most specific of the three rate dimensions: a scheme's negotiated rate beats a person-type
 * concession, which beats an admission-type rate.
 */
export function InsuranceSchemesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<InsuranceSchemeDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("catalog.insurancescheme.manage"));

  const query = useQuery({
    queryKey: ["insurance-schemes", search, pagination.page, pagination.pageSize],
    queryFn: () => insuranceSchemesApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Insurance scheme deleted" },
    mutationFn: (id: string) => insuranceSchemesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["insurance-schemes"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: InsuranceSchemeDto) => { setEditing(row); setFormOpen(true); };
  const handleDelete = (row: InsuranceSchemeDto) => {
    if (confirm(`Delete scheme "${row.code} — ${row.name}"? Blocked if any charge row uses it.`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const columns: GridColDef<InsuranceSchemeDto>[] = [
    { field: "code", headerName: "Code", width: 130 },
    { field: "name", headerName: "Scheme", flex: 1, minWidth: 220 },
    { field: "payerName", headerName: "Payer / TPA", flex: 1, minWidth: 180,
      valueGetter: (_v, row) => row.payerName || "—" },
    { field: "customerName", headerName: "Bills to", flex: 1, minWidth: 180,
      valueGetter: (_v, row) => row.customerName || "—" },
    {
      field: "isActive", headerName: "Status", width: 110, sortable: false,
      renderCell: (p) => <StatusPill label={p.row.isActive ? "Active" : "Inactive"} />,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage, onClick: () => openEdit(p.row) },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Insurance Schemes"
        subtitle="Payer plans with their own negotiated rate card. The narrowest of the three service charge rate dimensions."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New scheme</Button>
        ) : undefined}
      />
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code, scheme or payer"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <SchemeDialog open={formOpen} onClose={() => setFormOpen(false)} row={editing} />
    </Box>
  );
}

function SchemeDialog({ open, onClose, row }: { open: boolean; onClose: () => void; row: InsuranceSchemeDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [payerName, setPayerName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [customer, setCustomer] = useState<CustomerDto | null>(null);

  // The scheme stores only a customer id, so an edit has to fetch the row back to show its name.
  const customerQuery = useQuery({
    queryKey: ["customers", "resolve", row?.customerId],
    queryFn: () => customersApi.get(row!.customerId!),
    enabled: open && !!row?.customerId,
  });

  useEffect(() => {
    if (!open) return;
    setCode(row?.code ?? "");
    setName(row?.name ?? "");
    setPayerName(row?.payerName ?? "");
    setDescription(row?.description ?? "");
    setIsActive(row?.isActive ?? true);
    setCustomer(null);
  }, [open, row]);

  useEffect(() => {
    if (customerQuery.data) setCustomer(customerQuery.data);
  }, [customerQuery.data]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Scheme updated" : "Scheme created" },
    mutationFn: async () => {
      const payload = {
        code: code.trim(), name: name.trim(),
        payerName: payerName.trim() || null,
        customerId: customer?.id ?? null,
        description: description.trim() || null,
        isActive,
      };
      if (isEdit && row) { await insuranceSchemesApi.update(row.id, payload); return row.id; }
      const r = await insuranceSchemesApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["insurance-schemes"] });
      onClose();
    },
  });

  const err = !code.trim() ? "Code required" : !name.trim() ? "Scheme name required" : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit scheme" : "New insurance scheme"}</ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value)} inputProps={{ maxLength: 50 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField label="Scheme name *" size="small" fullWidth
                value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 200 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Payer / TPA" size="small" fullWidth
                value={payerName} onChange={(e) => setPayerName(e.target.value)}
                inputProps={{ maxLength: 200 }}
                helperText="Insurer or scheme operator" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomerPicker label="Bills to (customer)" value={customer} onChange={setCustomer}
                helperText="Optional — link when the payer is set up as a customer" />
            </Grid>
            <Grid size={12}>
              <TextField label="Description" size="small" fullWidth multiline minRows={2}
                value={description} onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 500 }} />
            </Grid>
            <Grid size={12}>
              <FormControlLabel label="Active"
                control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />} />
            </Grid>
          </Grid>
          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
